import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from './Context/UserContext';
import AppLayout from './AppLayout';
import logo from '../assets/Mindteck Logo.png';
import '../styles/Home.css';

const CAPABILITIES = [
    'Faster approvals',
    'Real-time invoice tracking',
    'Role-based access',
    'ERP integration',
    'Automatic notifications',
];

const CARDS = [
    {
        title: 'Why the VMS exists',
        items: [
            'Approvals run themselves instead of over email',
            'Vendors and invoices are trackable at any moment',
            'Every stage leaves a compliance record',
            'One workflow, whatever your role',
            'Approved invoices hand off to the ERP',
        ],
    },
    {
        title: 'How it works',
        ordered: true,
        items: [
            'A vendor registers and uploads statutory documents',
            'Verifier, PO and department head review in turn',
            'The vendor raises invoices against approved orders',
            'Finance and the CFO release the payment',
        ],
    },
    {
        title: 'What you get',
        items: [
            'Vendor and user management',
            'Multi-level approval workflows',
            'Invoice status at every stage',
            'Role-based access control',
            'Reminders before anything lapses',
        ],
    },
];

// The content shown both on the public landing and, when signed in, inside the
// app shell.
function HomeContent({ dashboardHref, signedIn }) {
    return (
        <>
            {/* ── Hero ─────────────────────────────────────────────── */}
            <section className="home-hero">
                <div className="home-hero-grid" aria-hidden="true" />
                <div className="home-hero-inner">
                    <p className="home-hero-eyebrow">Mindteck</p>
                    <h1 className="home-hero-title">
                        Vendor management,<br />end to end.
                    </h1>
                    <p className="home-hero-body">
                        Register vendors, verify their statutory documents, move invoices
                        through every level of approval, and see exactly where each one
                        stands — in one auditable system.
                    </p>
                    <div className="home-hero-actions">
                        <Link to={dashboardHref} className="vms-btn vms-btn-primary home-cta">
                            {signedIn ? 'Go to dashboard' : 'Sign in'}
                        </Link>
                        {!signedIn && (
                            <Link to="/sign-up" className="vms-btn vms-btn-ghost home-cta">
                                Register as a vendor
                            </Link>
                        )}
                    </div>
                    <ul className="home-pills">
                        {CAPABILITIES.map(c => (
                            <li className="home-pill" key={c}>{c}</li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ── Three information cards ─────────────────────────── */}
            <section className="home-cards">
                {CARDS.map(card => (
                    <article className="home-card" key={card.title}>
                        <h2 className="home-card-title">{card.title}</h2>
                        {card.ordered ? (
                            <ol className="home-steps">
                                {card.items.map(i => <li key={i}>{i}</li>)}
                            </ol>
                        ) : (
                            <ul className="home-list">
                                {card.items.map(i => <li key={i}>{i}</li>)}
                            </ul>
                        )}
                    </article>
                ))}
            </section>

            {/* ── Mission ─────────────────────────────────────────── */}
            <section className="home-mission">
                <div className="home-mission-copy">
                    <h2 className="home-mission-title">Our mission</h2>
                    <p className="home-mission-text">
                        Modernise vendor management through automation and transparency —
                        less manual effort, better compliance, and a clear record of who
                        approved what, and when.
                    </p>
                </div>
                <Link to={dashboardHref} className="home-mission-cta">
                    {signedIn ? 'Open dashboard' : 'Sign in'}
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                    </svg>
                </Link>
            </section>

            {/* ── Footer ──────────────────────────────────────────── */}
            <footer className="home-foot">
                <p className="home-foot-addr">
                    A.M.R. Tech Park, Bangalore &ndash; 560068
                    <span className="home-foot-dot" aria-hidden="true">·</span>
                    <span className="vms-num">080 41548000</span>
                </p>
                <a href="https://mindteck.com" target="_blank" rel="noreferrer" className="home-foot-link">
                    mindteck.com
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                </a>
            </footer>
        </>
    );
}

export function Home() {
    const { user } = useUser();

    useEffect(() => {
        // `is-home` lets Home.css paint the shell white for this page only —
        // the other shell screens keep the app background from theme.css.
        document.body.classList.add('no-global-nav', 'is-home');
        return () => document.body.classList.remove('no-global-nav', 'is-home');
    }, []);

    // ── Signed in: render inside the app shell (full-height sidebar) ──
    if (user) {
        return (
            <AppLayout title="Vendor Management" titleAccent="System">
                <div className="vms-home">
                    <HomeContent dashboardHref="/master-page" signedIn />
                </div>
            </AppLayout>
        );
    }

    // ── Public landing (signed out) ──
    return (
        <div className="vms-home">
            <div className="home-topbar">
                <Link to="/" className="home-logo-link" aria-label="Mindteck home">
                    <img src={logo} alt="Mindteck" className="home-logo" />
                </Link>
                <span className="home-topbar-name">Vendor Management System</span>
                <nav className="home-nav" aria-label="Account">
                    <Link to="/login" className="home-nav-link">Sign in</Link>
                    <Link to="/sign-up" className="home-nav-cta">Register</Link>
                </nav>
            </div>
            <HomeContent dashboardHref="/login" signedIn={false} />
        </div>
    );
}

export default Home;
